







































































































































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import Card from '@/components/Card.vue';
import {
  DeleteEventDTO,
  Event,
  EventStatus,
  JbicEntityType,
  UpdateEventStatusManageDTO,
  UpdateEventStatusSubmissionDTO
} from '@/store/modules/admin/types/admin.types';
import TableActionDropdown from '@/components/TableActionDropdown.vue';
import {
  LocationType,
  VenueFieldData
} from '@/store/modules/admin/types/group-level-attribute.types';
import dayjs from 'dayjs';
import { Action, State } from 'vuex-class';
import { RootState } from '@/store/store';
import { ApiState } from '@/store/types/general.types';
import GeneralModal from '@/components/GeneralModal.vue';
import { ToastProgrammatic } from 'buefy';
import DeclineReasonModal from './DeclineReasonModal.vue';
import { checkJbicPermission } from '@/utils/rbac.util';
import { JbicPermissions } from '@/store/modules/roles-and-permissions/types/roles-and-permissions.types';

@Component({
  components: {
    Card,
    TableActionDropdown,
    DeclineReasonModal
  }
})
export default class EventCard extends Vue {
  @Prop() public event!: Event;

  @Action('admin/deleteJbicEvent')
  private deleteJbicEvent!: (event: DeleteEventDTO) => Promise<void>;

  @State(({ admin }: RootState) => admin.apiState.deleteJbicEvent)
  private deleteJbicEventState!: ApiState;

  @Action('admin/submitEventForApproval')
  private submitEventForApproval!: (
    payload: UpdateEventStatusSubmissionDTO
  ) => Promise<void>;

  @State(({ admin }: RootState) => admin.apiState.submitEventForApproval)
  private submitEventForApprovalState!: ApiState;

  @Action('admin/updateManagedEventStatus')
  private updateManagedEventStatus!: (params: {
    eventId: number;
    payload: UpdateEventStatusManageDTO;
  }) => Promise<void>;

  @State(({ admin }: RootState) => admin.apiState.updateManagedEventStatus)
  private updateManagedEventStatusState!: ApiState;

  get eventBannerImage() {
    return this.event.bannerImageUrl; // TODO: have a default photo if no event banner
  }

  get eventShortMonth() {
    if (!this.event.startAt) {
      return '';
    }

    const date = new Date(this.event.startAt);

    return dayjs(date).format('MMM');
  }

  get eventYear() {
    if (!this.event.startAt) {
      return '';
    }

    const date = new Date(this.event.startAt);

    return date.getFullYear();
  }

  get eventDate() {
    if (!this.event.startAt || !this.event.endAt) {
      return '';
    }

    const startDate = dayjs(new Date(this.event.startAt)).format('DD MMM YYYY');
    const endDate = dayjs(new Date(this.event.endAt)).format('DD MMM YYYY');

    return `${startDate} - ${endDate}`;
  }

  get eventLocation() {
    if (!this.event.location) {
      return '';
    }

    const locationType: LocationType = this.event.location.locationType;
    if (locationType === LocationType.REMOTE) {
      return 'Remote Attendance';
    }

    const venueDetails: VenueFieldData | undefined =
      this.event.location.venueDetails || undefined;
    if (!venueDetails) {
      return '';
    }

    let location: string = '';
    if (venueDetails.country) {
      location = venueDetails.country;

      if (venueDetails.state) {
        location = `${venueDetails.state}, ${location}`;
      }
    }

    if (!location) {
      return 'Remote Attendance';
    }

    return location;
  }

  get eventTitle() {
    return this.event.title;
  }

  get eventIsEnded(): boolean {
    return this.event.isEnded;
  }

  get isJbicEventCreateRole(): boolean {
    return checkJbicPermission(
      JbicPermissions.EVENT_CREATE,
      this.event.group.id
    );
  }

  get isJbicEventUpdateRole(): boolean {
    return checkJbicPermission(
      JbicPermissions.EVENT_UPDATE,
      this.event.group.id
    );
  }

  get isJbicEventDeleteRole(): boolean {
    return checkJbicPermission(
      JbicPermissions.EVENT_DELETE,
      this.event.group.id
    );
  }

  get isJbicEventViewRole(): boolean {
    return checkJbicPermission(JbicPermissions.EVENT_READ, this.event.group.id);
  }

  get isJbicEventManageRole(): boolean {
    return checkJbicPermission(
      JbicPermissions.EVENT_MANAGE,
      this.event.group.id
    );
  }

  get isActionDropdownHidden() {
    // when the action dropdown should be hidden
    // 1. isJbiAdmin === true and the EventStatus is either APPROVED or DENIED
    if (
      !this.isJbicEventUpdateRole &&
      (this.isEventStatus(EventStatus.APPROVED) ||
        this.isEventStatus(EventStatus.DECLINED))
    ) {
      return true;
    }

    if (!this.isJbicEventCreateRole && this.isEventStatus(EventStatus.DRAFT)) {
      return true;
    }

    return false;
  }

  isEventStatus(status: EventStatus): boolean {
    return this.event.status === status;
  }

  goToDetails() {
    this.$router.push({
      name: 'jbic-event-view',
      params: {
        eventId: this.event.id.toString(),
        groupId: this.event.group.id.toString()
      }
    });
  }

  editEvent(): void {
    this.$router.push({
      name: 'jbic-create-event',
      params: {
        eventId: this.event.id.toString(),
        groupId: this.event.group.id.toString(),
        changeType: 'EDIT'
      }
    });
  }

  //Return toast error
  submitEventForApprovalHandler(): void {
    // Validate all required fields are filled before submitting for approval
    if (!this.isEventCompleteForApproval()) {
      ToastProgrammatic.open({
        message: 'Please fill in all required fields before submitting.',
        type: 'is-danger',
        position: 'is-top'
      });
      return;
    }
    const payload: UpdateEventStatusSubmissionDTO = {
      status: EventStatus.AWAITING_APPROVAL,
      id: this.event.id
    };
    this.submitEventForApproval(payload);
  }

  private isEventCompleteForApproval(): boolean {
    const event = this.event;
    return !!(
      event?.title &&
      event?.slogan &&
      event?.startAt &&
      event?.endAt &&
      event?.timezone &&
      event?.description &&
      event?.location &&
      event?.urlToEvent &&
      event?.urlToHost &&
      event?.contacts &&
      event?.bannerImageUrl &&
      event?.teaserImageUrl
    );
  }
  approveEvent(): void {
    // only MANAGE should be able to approve and decline
    if (!this.isJbicEventManageRole) {
      return;
    }

    const payload: UpdateEventStatusManageDTO = {
      status: EventStatus.APPROVED
    };
    this.updateManagedEventStatus({ eventId: this.event.id, payload });
  }

  declineEvent(): void {
    // only MANAGE should be able to approve and decline
    if (!this.isJbicEventManageRole) {
      return;
    }

    this.$buefy.modal.open({
      parent: this,
      component: DeclineReasonModal,
      hasModalCard: true,
      trapFocus: true,
      props: {
        entityType: JbicEntityType.EVENT
      },
      events: {
        confirm: (declineReason: string) => {
          const payload: UpdateEventStatusManageDTO = {
            status: EventStatus.DECLINED,
            declinedReason: declineReason
          };
          this.updateManagedEventStatus({ eventId: this.event.id, payload });
        }
      }
    });
  }

  publishEvent(): void {
    if (!this.isJbicEventUpdateRole) {
      return;
    }

    const payload: UpdateEventStatusSubmissionDTO = {
      status: EventStatus.PUBLISHED,
      id: this.event.id
    };
    this.submitEventForApproval(payload);
  }

  unpublishEvent(): void {
    if (!this.isJbicEventUpdateRole) {
      return;
    }

    const payload: UpdateEventStatusSubmissionDTO = {
      status: EventStatus.DRAFT,
      id: this.event.id
    };
    this.submitEventForApproval(payload);
  }

  deleteEvent(): void {
    if (!this.isJbicEventDeleteRole) {
      return;
    }

    this.$buefy.modal.open({
      parent: this,
      component: GeneralModal,
      hasModalCard: true,
      trapFocus: true,
      props: {
        modalTitle: 'Delete selected event?',
        modalContent:
          'This will permanently delete the selected event. This action cannot be undone.',
        confirmBtnText: 'Delete',
        cancelBtnText: 'Cancel'
      },
      events: {
        confirm: () => {
          this.deleteJbicEvent({ id: this.event.id });
        }
      }
    });
  }

  @Watch('deleteJbicEventState')
  onDeleteJbicEventState() {
    if (this.deleteJbicEventState.error) {
      ToastProgrammatic.open({
        message: `An error occurred while deleting the event. Please try again.`,
        type: 'is-danger',
        position: 'is-top'
      });

      return;
    }

    if (
      this.deleteJbicEventState.loading ||
      !this.deleteJbicEventState.success
    ) {
      return;
    }

    ToastProgrammatic.open({
      message: `The event has been deleted successfully.`,
      type: 'is-success',
      position: 'is-top'
    });

    this.emitRefreshEvent();
  }

  @Watch('submitEventForApprovalState')
  onSubmitEventForApprovalState() {
    if (this.submitEventForApprovalState.error) {
      const err: any = this.submitEventForApprovalState.error;
      ToastProgrammatic.open({
        message:
          err?.response?.data?.message ||
          'An error occurred while submitting the event for approval. Please try again.',
        type: 'is-danger',
        position: 'is-top'
      });

      return;
    }

    if (
      this.submitEventForApprovalState.loading ||
      !this.submitEventForApprovalState.success
    ) {
      return;
    }

    ToastProgrammatic.open({
      message: `The event status has been updated successfully.`,
      type: 'is-success',
      position: 'is-top'
    });

    this.emitRefreshEvent();
  }

  @Watch('updateManagedEventStatusState')
  onUpdateManagedEventStatusState() {
    if (this.updateManagedEventStatusState.error) {
      ToastProgrammatic.open({
        message: `An error occurred while updating the event status. Please try again.`,
        type: 'is-danger',
        position: 'is-top'
      });

      return;
    }

    if (
      this.updateManagedEventStatusState.loading ||
      !this.updateManagedEventStatusState.success
    ) {
      return;
    }

    ToastProgrammatic.open({
      message: `The event status has been updated successfully.`,
      type: 'is-success',
      position: 'is-top'
    });

    this.emitRefreshEvent();
  }

  emitRefreshEvent() {
    this.$parent?.$parent?.$emit('refreshEvent');
  }
}
